import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MatTableDataSource } from '@angular/material/table';
import { ApiService, UserListItem } from '../../../shared/services/api.service';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AddEditUserDialogComponent } from './add-edit-user-dialog.component';
import { IdDocumentPreviewDialogComponent } from './id-document-preview-dialog.component';
import { AuthService, User } from '../../../core/auth/auth.service';
import { Observable } from 'rxjs';

@Component({
  selector: 'app-users',
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.css']
})
export class UsersComponent implements OnInit {
  readonly invitePendingTooltip = 'Invite sent, account has not been set up yet';
  readonly paymentDueTooltip = 'Payment is due';
  readonly roleFilterOptions: Array<{ value: 'user' | 'admin' | 'billing_owner'; label: string }> = [
    { value: 'user', label: 'User' },
    { value: 'admin', label: 'Admin' },
    { value: 'billing_owner', label: 'Billing Owner' },
  ];
  readonly statusFilterOptions: Array<{
    value: 'approved' | 'invited_pending' | 'pending_approval' | 'rejected' | 'disabled';
    label: string;
  }> = [
    { value: 'approved', label: 'Approved' },
    { value: 'invited_pending', label: 'Invited / Pending' },
    { value: 'pending_approval', label: 'Pending Approval' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'disabled', label: 'Disabled' },
  ];
  displayedColumns: string[] = ['name', 'surname', 'account', 'email', 'role', 'credits', 'enabled', 'actions'];
  dataSource = new MatTableDataSource<UserListItem>([]);
  loading = false;
  selectedRoles: Array<'user' | 'admin' | 'billing_owner'> = [];
  selectedStatuses: Array<'approved' | 'invited_pending' | 'pending_approval' | 'rejected' | 'disabled'> = [];
  currentUser$: Observable<User | null>;
  currentUserId: string | null = null;
  currentUserIsPlatformAdmin = false;
  canCurrentUserMasquerade = false;

  constructor(
    private apiService: ApiService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private authService: AuthService,
    private router: Router
  ) {
    this.currentUser$ = this.authService.getCurrentUser();
  }

  ngOnInit(): void {
    this.dataSource.filterPredicate = (user, filter: string): boolean => this.matchesUserFilters(user, filter);
    // Get current user ID
    this.currentUser$.subscribe(user => {
      this.currentUserId = user?.uid || null;
      this.currentUserIsPlatformAdmin = user?.isPlatformAdmin === true;
      this.canCurrentUserMasquerade =
        (!this.currentUserIsPlatformAdmin || this.authService.resolveAppMode(user) === 'org') &&
        user?.role === 'admin' &&
        user?.features?.['masquerade_as'] === true;
    });
    this.loadUsers();
  }

  isCurrentUser(user: UserListItem): boolean {
    return this.currentUserId !== null && user.userId === this.currentUserId;
  }

  applyFilters(): void {
    this.dataSource.filter = JSON.stringify({
      roles: this.selectedRoles,
      statuses: this.selectedStatuses,
    });
  }

  clearFilters(): void {
    this.selectedRoles = [];
    this.selectedStatuses = [];
    this.applyFilters();
  }

  hasActiveFilters(): boolean {
    return this.selectedRoles.length > 0 || this.selectedStatuses.length > 0;
  }

  private matchesUserFilters(user: UserListItem, rawFilter: string): boolean {
    if (!rawFilter) return true;

    let parsed: { roles?: string[]; statuses?: string[] };
    try {
      parsed = JSON.parse(rawFilter);
    } catch {
      return true;
    }

    const selectedRoles = parsed.roles ?? [];
    const selectedStatuses = parsed.statuses ?? [];
    const userRoles = this.resolveUserRoles(user);
    const userStatuses = this.resolveUserStatuses(user);

    const roleMatch = selectedRoles.length === 0 || selectedRoles.some(role => userRoles.includes(role));
    const statusMatch =
      selectedStatuses.length === 0 || selectedStatuses.some(status => userStatuses.includes(status));

    return roleMatch && statusMatch;
  }

  private resolveUserRoles(user: UserListItem): string[] {
    const normalizedRole = (user.role || '').toLowerCase();
    const roles = [normalizedRole === 'admin' ? 'admin' : 'user'];
    if (user.isBillingOwner === true) {
      roles.push('billing_owner');
    }
    return roles;
  }

  private resolveUserStatuses(user: UserListItem): string[] {
    const statuses: string[] = [];
    if (user.approvalStatus === 'APPROVED' && !this.isInvitePending(user)) {
      statuses.push('approved');
    }
    if (this.isInvitePending(user)) {
      statuses.push('invited_pending');
    }
    if (this.isAwaitingApproval(user)) {
      statuses.push('pending_approval');
    }
    if (this.isRejected(user)) {
      statuses.push('rejected');
    }
    if (user.enabled === false) {
      statuses.push('disabled');
    }
    return statuses;
  }

  isInvitePending(user: UserListItem): boolean {
    return user.invitePending === true;
  }

  getAccountLabel(user: UserListItem): string {
    const planLabel = (user.selectedPlanDisplayName || '').trim();
    if (planLabel) return planLabel;
    return user.team === true ? 'Team (no plan selected)' : 'Individual (no plan selected)';
  }

  isTrialUser(user: UserListItem): boolean {
    return user.selectedPlanCode === 'INDIVIDUAL_TRIAL';
  }

  hasPromoCampaign(user: UserListItem): boolean {
    return !!String(user.promoCampaignName || '').trim();
  }

  promoCampaignTooltip(user: UserListItem): string {
    const name = String(user.promoCampaignName || '').trim() || 'Campaign';
    const createdRaw = String(user.promoCampaignCreatedAt || '').trim();
    if (!createdRaw) return `Campaign: ${name}`;
    const created = new Date(createdRaw);
    if (Number.isNaN(created.getTime())) return `Campaign: ${name}`;
    return `Campaign: ${name} (${created.toLocaleDateString()})`;
  }

  getCurrentMonthYear(): string {
    const now = new Date();
    const month = now.toLocaleString('en-US', { month: 'long' });
    const year = now.getFullYear();
    return `${month} ${year}`;
  }

  loadUsers(): void {
    this.loading = true;
    this.apiService.listUsers().subscribe({
      next: (response: { users: UserListItem[] }) => {
        this.dataSource.data = response.users;
        this.loading = false;
      },
      error: (error: any) => {
        this.loading = false;
        let errorMessage = 'Failed to load users';
        if (error.error?.error?.message) {
          errorMessage = error.error.error.message;
        }
        this.snackBar.open(errorMessage, 'Close', { duration: 5000 });
      }
    });
  }

  addUser(): void {
    const dialogRef = this.dialog.open(AddEditUserDialogComponent, {
      width: '500px',
      data: {
        mode: 'add'
      }
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        this.loadUsers();
      }
    });
  }

  editUser(user: UserListItem): void {
    const dialogRef = this.dialog.open(AddEditUserDialogComponent, {
      width: '500px',
      data: {
        mode: 'edit',
        user
      }
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        this.loadUsers();
      }
    });
  }

  isPending(user: UserListItem): boolean {
    return user.approvalStatus === 'PENDING';
  }

  isAwaitingApproval(user: UserListItem): boolean {
    return this.isPending(user) && !this.isInvitePending(user);
  }

  isPendingPayment(user: UserListItem): boolean {
    return this.isAwaitingApproval(user) && user.activationPendingPayment === true;
  }

  canResendInvite(user: UserListItem): boolean {
    // Resend should be available for any not-yet-completed setup,
    // including auto-approved invites that are still unclaimed.
    return this.isInvitePending(user) && !this.isRejected(user);
  }

  canActionApproval(user: UserListItem): boolean {
    return this.isAwaitingApproval(user);
  }

  isRejected(user: UserListItem): boolean {
    return user.approvalStatus === 'REJECTED';
  }

  approveUser(user: UserListItem): void {
    this.apiService.updateUser(user.userId, { approvalStatus: 'APPROVED' }).subscribe({
      next: () => {
        this.snackBar.open('User approved successfully', 'Close', { duration: 3000 });
        this.loadUsers();
      },
      error: (error: any) => {
        const paymentRef = String(error?.error?.seatActivationPaymentReference || '').trim();
        const requiresPayment = error?.error?.requiresSeatActivationPayment === true && Boolean(paymentRef);
        if (requiresPayment) {
          this.apiService.initPaymentMethod('paystack', { seatActivationPaymentReference: paymentRef }).subscribe({
            next: (initResponse: any) => {
              const authUrl = String(initResponse?.authorizationUrl || '').trim();
              if (authUrl) {
                window.open(authUrl, '_blank', 'noopener');
                this.snackBar.open('Approval is pending payment. Complete checkout to finalize.', 'Close', {
                  duration: 6000,
                });
                this.loadUsers();
                return;
              }
              this.snackBar.open('Payment initialization failed for seat approval', 'Close', { duration: 6000 });
            },
            error: (initError: any) => {
              this.snackBar.open(
                initError?.error?.error?.message || 'Failed to initialize approval payment',
                'Close',
                { duration: 6000 }
              );
            },
          });
          return;
        }
        this.snackBar.open(error?.error?.error?.message || 'Failed to approve user', 'Close', { duration: 5000 });
      },
    });
  }

  rejectUser(user: UserListItem): void {
    this.apiService.updateUser(user.userId, { approvalStatus: 'REJECTED' }).subscribe({
      next: () => {
        this.snackBar.open('User rejected successfully', 'Close', { duration: 3000 });
        this.loadUsers();
      },
      error: (error: any) => {
        this.snackBar.open(error?.error?.error?.message || 'Failed to reject user', 'Close', { duration: 5000 });
      },
    });
  }

  viewIdDocument(user: UserListItem): void {
    this.apiService.getUserIdDocumentUrl(user.userId).subscribe({
      next: (response) => {
        this.dialog.open(IdDocumentPreviewDialogComponent, {
          width: '90vw',
          maxWidth: '1100px',
          data: {
            userEmail: user.email || null,
            phone: response.phone || user.phone || null,
            industry: response.industry || user.industry || null,
            industryOther: response.industryOther || user.industryOther || null,
            ffcDocumentPath: response.ffcDocumentPath || user.ffcDocumentPath || null,
            ffcSignedUrl: response.ffcSignedUrl || null,
            ffcExpiresAt: response.ffcExpiresAt || null,
            idDocumentPath: response.idDocumentPath || user.idDocumentPath || null,
            signedUrl: response.signedUrl,
            expiresAt: response.expiresAt,
          },
        });
      },
      error: (error: any) => {
        this.snackBar.open(error?.error?.error?.message || 'Failed to load ID document preview', 'Close', { duration: 5000 });
      },
    });
  }

  deleteUser(user: UserListItem): void {
    if (confirm(`Are you sure you want to delete user ${user.email}?`)) {
      this.apiService.deleteUser(user.userId).subscribe({
        next: () => {
          this.snackBar.open('User deleted successfully', 'Close', { duration: 3000 });
          this.loadUsers();
        },
        error: (error: any) => {
          let errorMessage = 'Failed to delete user';
          if (error.error?.error?.message) {
            errorMessage = error.error.error.message;
          }
          this.snackBar.open(errorMessage, 'Close', { duration: 5000 });
        }
      });
    }
  }

  viewAuditLog(user: UserListItem): void {
    void this.router.navigate(['/admin/users', user.userId, 'audit-log'], {
      queryParams: user.email ? { email: user.email } : {}
    });
  }

  canMasquerade(user: UserListItem): boolean {
    return (
      this.canCurrentUserMasquerade &&
      !this.isCurrentUser(user) &&
      !this.isInvitePending(user) &&
      user.enabled === true &&
      user.approvalStatus === 'APPROVED'
    );
  }

  masqueradeAs(user: UserListItem): void {
    const ok = this.authService.startMasquerade({
      userId: user.userId,
      email: user.email || null,
      name: user.name || null,
      surname: user.surname || null,
    });
    if (!ok) {
      const message =
        this.currentUserIsPlatformAdmin && this.authService.getPreferredAppMode() !== 'org'
          ? 'Switch to Organization mode to use masquerade'
          : 'Masquerade is not enabled for your account';
      this.snackBar.open(message, 'Close', { duration: 5000 });
      return;
    }
    this.apiService.requestUserInfoSummaryRefresh();
    this.snackBar.open(`Masquerading as ${user.email || user.userId}`, 'Close', { duration: 5000 });
    void this.router.navigate(['/admin/address-book']);
  }

  resetPassword(user: UserListItem): void {
    if (confirm(`Send password reset email to ${user.email}?`)) {
      this.apiService.resetUserPassword(user.userId).subscribe({
        next: () => {
          this.snackBar.open(`Password reset email sent to ${user.email}`, 'Close', { duration: 5000 });
        },
        error: (error: any) => {
          let errorMessage = 'Failed to send password reset email';
          if (error.error?.error?.message) {
            errorMessage = error.error.error.message;
          }
          this.snackBar.open(errorMessage, 'Close', { duration: 5000 });
        }
      });
    }
  }

  resendInvite(user: UserListItem): void {
    if (confirm(`Resend invite email to ${user.email}?`)) {
      this.apiService.resendUserInvite(user.userId).subscribe({
        next: () => {
          this.snackBar.open(`Invite email resent to ${user.email}`, 'Close', { duration: 5000 });
        },
        error: (error: any) => {
          let errorMessage = 'Failed to resend invite';
          if (error.error?.error?.message) {
            errorMessage = error.error.error.message;
          }
          this.snackBar.open(errorMessage, 'Close', { duration: 5000 });
        }
      });
    }
  }

  toggleEnabled(user: UserListItem): void {
    const newEnabled = !user.enabled;
    this.apiService.updateUser(user.userId, { enabled: newEnabled }).subscribe({
      next: () => {
        user.enabled = newEnabled;
        this.snackBar.open(`User ${newEnabled ? 'enabled' : 'disabled'} successfully`, 'Close', { duration: 3000 });
      },
      error: (error: any) => {
        let errorMessage = 'Failed to update user';
        if (error.error?.error?.message) {
          errorMessage = error.error.error.message;
        }
        this.snackBar.open(errorMessage, 'Close', { duration: 5000 });
      }
    });
  }
}

